import { Component, OnInit } from '@angular/core';
import { AmorphicService } from '../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { Controller } from '../../Controller';
import { Person } from '../../../../../common/js/customer/Person';
import { Router } from '@angular/router';
import { CoverpathUtils } from '../../../../../coverpath-common/js/static/CoverpathUtils';
import { Agent } from '../../../../../common/js/customer/Agent';
import { FormatterService } from '../../../../../coverpath-common/angular/services/formatter/formatter.service';
import { SafeInviteInfo } from '../../../../../common/js/customer/SafeInviteInfo';
import { SafeAgent } from '../../../../../common/js/customer/SafePolicyInfo';
import { CommonRoutes } from '../../../../../coverpath-common/angular/services/authentication/auth0.service';
import { WindowService } from '../../../../../common/angular-shared/services/window/window.service';
import { GenesysContext, GenesysParticipantData } from '../../../../../common/js/types/CommonTypes';
import { HelpContactInfo, PrimaryAgentAndPolicies } from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { AccountCenterService } from '../../../../../coverpath-common/angular/services/account-center/account-center.service';
import { CoverpathURLs } from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import type { Policy } from '../../../../../common/js/customer/Policy';
import type { CasApplication } from '../../../../../coverpath-common/classes/coverpath_subclasses/CasApplication';

const INVALID_LINK_URL = '/invalid-link';
const DRAFT_IN_PROGRESS = '/draft-in-progress';
const INVALID_EMAIL_URL = `/${CommonRoutes.INVALID_EMAIL}`;

@Component({
  moduleId: module.id,
  selector: 'help',
  templateUrl: './help.component.html',
})
export class HelpComponent implements OnInit {
  controller: Controller;
  clientHasAgent: boolean;
  safeAgent: SafeAgent;
  pageHeadingText: string;
  supportHeadingText: string;
  helpContactInfo: HelpContactInfo[];
  advisorDescriptiveNoun: string;
  singularAdvisorDescriptiveNoun: string;
  draftApp: boolean;
  invalidQuoteEmail: boolean;
  safeInviteinfo: SafeInviteInfo;
  genesysParticipantData: GenesysParticipantData;
  nbDecommissionPost30Days: boolean = false;

  constructor(
    private amorphicService: AmorphicService,
    private formatterService: FormatterService,
    private windowService: WindowService,
    private readonly router: Router,
    private accountCenterService: AccountCenterService
  ) {
    this.controller = amorphicService.controller as Controller;
  }

  async ngOnInit(): Promise<void> {
    // HL-17855 'financial professional'
    const customer = this.controller.customer;
    this.genesysParticipantData = {
      'WP-Authenticated': customer.email ? true : false,
      'WP-Context': GenesysContext.COVERPATH_CLIENT,
      'WP-First-Name': customer.firstName,
      'WP-Last-Name': customer.lastName,
      'WP-Email': customer.email,
      'WP-Is-1851': false,
    };
    this.advisorDescriptiveNoun = CoverpathUtils.getAdvisorDescriptiveNoun();
    this.singularAdvisorDescriptiveNoun = `a ${CoverpathUtils.getAdvisorDescriptiveNoun()}`;
    let primaryPolicyAgents: PrimaryAgentAndPolicies[];

    this.safeInviteinfo = await this.controller.inviteController.getSafeInviteInfo();
    const application = customer.getRecentlyCreatedApplication();

    if (application && application.primaryPolicy) {
      primaryPolicyAgents = customer.primaryPolicyAgents;
    } else if (!!this.safeInviteinfo) {
      this.safeAgent = this.safeInviteinfo.safePolicyInfo.primaryAgent;
    }

    const hasPrimaryPolicyAgents = !!primaryPolicyAgents && primaryPolicyAgents.length > 0;
    this.clientHasAgent = hasPrimaryPolicyAgents || !!this.safeAgent;

    this.nbDecommissionPost30Days = this.controller.nbDecommissionPost30Days;
    if (!this.clientHasAgent && this.nbDecommissionPost30Days) {
      this.windowService.nativeWindow.location.href = 'https://financialprofessionals.massmutual.com/';
      return;
    }

    if (hasPrimaryPolicyAgents) {
      this.helpContactInfo = primaryPolicyAgents.map((primaryPolicyAgent) => {
        const agentPhoneLink = primaryPolicyAgent.agent.person.phone?.number;
        return {
          name: primaryPolicyAgent.agent.displayNameWithLastName,
          email: primaryPolicyAgent.agent.person.email,
          phoneLink: agentPhoneLink,
          phone: this.formatterService.format('telephone', agentPhoneLink),
          policies: primaryPolicyAgent.policies,
        };
      });
    }

    if (!!this.safeAgent) {
      this.helpContactInfo = [
        {
          name: this.safeAgent.displayNameWithLastName,
          email: this.safeAgent.email,
          phoneLink: this.safeAgent.phoneNumber,
          phone: this.formatterService.format('telephone', this.safeAgent.phoneNumber),
          policies: [],
        },
      ];
    }

    let invalidLinkMessage: string;
    if (
      this.router.url === INVALID_LINK_URL ||
      this.router.url === DRAFT_IN_PROGRESS ||
      this.router.url === INVALID_EMAIL_URL
    ) {
      invalidLinkMessage = `We can't locate the quote you're looking for.`;
      if (this.router.url === DRAFT_IN_PROGRESS) {
        invalidLinkMessage = `Your financial professional is working on your application.`;
        this.draftApp = true;
      }

      if (this.router.url === INVALID_EMAIL_URL) {
        invalidLinkMessage = `The email you used does not match the email your financial professional used for this application.`;
        this.invalidQuoteEmail = true;
      }

      if (!this.controller.isLoggedIn()) {
        // @NOTE the proper fix would be to reset the session
        await this.controller.hardResetCustomer();
      }
    }

    this.setHelpPageText(this.clientHasAgent, invalidLinkMessage);
  }

  setHelpPageText(hasAgent: boolean, pageHeadingText = 'How can we help?'): void {
    if (hasAgent) {
      this.pageHeadingText = pageHeadingText;
      this.supportHeadingText = 'Questions about your application or policy?';
    } else {
      this.pageHeadingText = `We couldn't find your ${this.advisorDescriptiveNoun}`;
      this.supportHeadingText = `Good news: we can connect you with ${this.advisorDescriptiveNoun} for more information or to apply for coverage.`;
    }
  }

  getQueryParams(policy: Policy) {
    this.amorphicService.controller.customer.application = policy.application as CasApplication;
    return { applicationNumber: policy.application.applicationNumber };
  }
}
